import { useRef } from 'react';
import { X, Minus, Plus, ShoppingBag, Repeat, Sparkles, Package } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEffect, useState } from 'react';
import { fetchShopifyProducts, ShopifyProduct } from '@/lib/shopify';
import tackyGripSprayHero from '@/assets/tacky-grip-spray-hero.webp';
import ExitIntentPopup from '@/components/ExitIntentPopup';

const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQuantity, removeItem, totalPrice, addItem } = useCart();
  const [featuredGrips, setFeaturedGrips] = useState<ShopifyProduct[]>([]);
  const [gripProProduct, setGripProProduct] = useState<ShopifyProduct | null>(null);
  const exitIntentTriggerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (isOpen && items.length === 0) {
      fetchShopifyProducts(50).then(data => {
        const handles = ['bright-white-overgrip', 'pro-tour-pink-gradient-overgrips', 'xtreme-tack-white'];
        const grips = handles.map(h => data.find(p => p.node.handle?.toLowerCase() === h)).filter(Boolean) as ShopifyProduct[];
        setFeaturedGrips(grips);
        const gripPro = data.find(p => p.node.handle?.toLowerCase().includes('grip-pro'));
        if (gripPro) setGripProProduct(gripPro);
      });
    }
  }, [isOpen, items.length]);

  const handleQuickAdd = (product: ShopifyProduct) => {
    const variant = product.node.variants?.edges?.[0]?.node;
    const price = parseFloat(variant?.price?.amount || product.node.priceRange?.minVariantPrice?.amount || '0');
    const image = product.node.images?.edges?.[0]?.node?.url || '';
    addItem({
      id: product.node.id,
      name: product.node.title,
      styleNumber: product.node.handle || '',
      price,
      color: '',
      image,
    });
  };

  /** Close cart; if there are items, trigger exit-intent popup */
  const handleClose = () => {
    if (items.length > 0 && exitIntentTriggerRef.current) {
      exitIntentTriggerRef.current();
    }
    closeCart();
  };

  return (
    <>
      {/* Exit-intent popup — only triggered from this drawer */}
      <ExitIntentPopup triggerRef={exitIntentTriggerRef} />

      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-secondary/60 z-50 animate-fade-in"
            onClick={handleClose}
          />

          {/* Drawer — full width on mobile, fixed 400px on sm+, never overflows viewport */}
          <div className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[min(400px,100vw)] bg-background z-50 shadow-2xl animate-slide-in-right flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-border flex-shrink-0">
              <div className="flex items-center space-x-2">
                <ShoppingBag className="w-5 h-5" />
                <h2 className="font-display font-bold text-lg sm:text-xl">Your Cart</h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-muted rounded-full transition-colors"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Cart Items */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-5">
              {items.length === 0 ? (
                <div className="flex flex-col h-full">
                  <div className="text-center mb-4">
                    <ShoppingBag className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-base font-medium">Your cart is empty</p>
                    <p className="text-muted-foreground text-sm">Check out our top picks!</p>
                  </div>

                  {/* Top 3 Featured Grips */}
                  {featuredGrips.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Top Grips
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {featuredGrips.map((product) => {
                          const price = parseFloat(product.node.priceRange?.minVariantPrice?.amount || '0');
                          const image = product.node.images?.edges?.[0]?.node?.url || '';
                          return (
                            <div key={product.node.id} className="bg-muted/50 rounded-lg overflow-hidden border border-border">
                              <div className="aspect-square overflow-hidden">
                                <img src={image} alt={product.node.title} className="w-full h-full object-cover" />
                              </div>
                              <div className="p-2">
                                <p className="text-[10px] font-medium leading-tight truncate">{product.node.title}</p>
                                <p className="text-[10px] text-muted-foreground">${price.toFixed(2)}</p>
                                <button
                                  onClick={() => handleQuickAdd(product)}
                                  className="mt-1 w-full text-[10px] sm:text-xs font-semibold rounded-full py-1.5 btn-fill-rise btn-fill-dark"
                                >
                                  + Add
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Grip Pro */}
                  {gripProProduct && (
                    <div className="mb-4">
                      <div className="flex items-center gap-3 bg-muted/50 rounded-lg border border-border p-3">
                        <img
                          src={gripProProduct.node.images?.edges?.[0]?.node?.url || tackyGripSprayHero}
                          alt="Grip Pro"
                          className="w-14 h-14 object-cover rounded-md flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{gripProProduct.node.title}</p>
                          <p className="text-[10px] text-muted-foreground">
                            ${parseFloat(gripProProduct.node.priceRange?.minVariantPrice?.amount || '0').toFixed(2)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleQuickAdd(gripProProduct)}
                          className="text-[10px] sm:text-xs font-semibold rounded-full px-3 py-1.5 btn-fill-rise btn-fill-dark flex-shrink-0"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Bundle CTA */}
                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-primary" />
                      <p className="text-xs font-bold">Bundle & Save 15%</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-2">
                      Grab a Comfort + Gradient grip combo and save!
                    </p>
                    <Button
                      size="sm"
                      className="w-full text-xs h-8"
                      onClick={() => {
                        closeCart();
                        window.location.href = '/shop-grips';
                      }}
                    >
                      Shop Bundles
                    </Button>
                  </div>

                  <div className="mt-auto pt-4">
                    <Button onClick={handleClose} variant="outline" className="w-full">
                      Continue Shopping
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={`${item.id}-${item.color}-${item.size || ''}-${item.subscription?.subscriptionKey || ''}`}
                      className="flex gap-3 p-3 sm:p-4 bg-muted/50 rounded-lg"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">{item.name}</h3>
                        <p className="text-xs text-muted-foreground">Style #{item.styleNumber}</p>

                        {/* Subscription Badge */}
                        {item.subscription?.enabled && (
                          <Badge className="mt-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[10px] sm:text-xs">
                            <Repeat className="w-3 h-3 mr-1" />
                            Subscribe: 3 months — ${item.subscription.pricePerPack.toFixed(2)}/{item.subscription.packType.includes('pack') ? 'pack' : 'bottle'}/mo
                          </Badge>
                        )}

                        <div className="flex items-center gap-2 mt-1">
                          {!item.subscription?.enabled && item.color && (
                            <div
                              className="w-4 h-4 rounded-full border border-border flex-shrink-0"
                              style={{ backgroundColor: item.color }}
                            />
                          )}
                          {item.size && (
                            <span className="text-xs font-medium bg-muted px-2 py-0.5 rounded">
                              Size: {item.size}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-1 hover:bg-background rounded transition-colors"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <span className="font-medium w-5 sm:w-6 text-center text-sm">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 hover:bg-background rounded transition-colors"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                          <span className="font-bold text-sm sm:text-base">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1 hover:text-destructive transition-colors self-start flex-shrink-0"
                        aria-label="Remove item"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer — pinned at bottom */}
            {items.length > 0 && (
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-border space-y-3 flex-shrink-0 bg-background">
                <div className="flex items-center justify-between text-base sm:text-lg">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-bold">${totalPrice.toFixed(2)}</span>
                </div>
                <Button className="w-full btn-fill-rise btn-fill-dark font-bold py-5 sm:py-6 text-sm sm:text-base rounded-full">
                  Checkout
                </Button>
                <button
                  onClick={handleClose}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default CartDrawer;
